/**
 * Returns true if the product has a default discount
 * @param {Object} product Product object with SFRA format
 * @returns {boolean} True if it has discount
 */
const hasDefaultDiscount = (product) =>
  !!(product.price && product.price.list !== null);

/**
 * Returns true if the product has a price range from price book
 * @param {Object} product Product object with SFRA format
 * @returns {boolean} True if it has discount
 */
const hasPriceRange = (product) =>
  !!(product.price && product.price.type === 'range');

/**
 * Returns an object with a product price info
 * @param {Object} price Price object with SFRA format
 * @returns {Object} Product price object
 */
const productPrice = ({ value, currency, formatted }) => ({
  currency,
  text: formatted,
  value,
});

export const productPriceMixin = {
  computed: {
    /**
     * Get the product discont in percentage
     * @returns {number} Number in range [0..100]
     */
    discountPercent() {
      return this.product.price.discount || 0;
    },

    /**
     * Get the product price without discount
     * @returns {Object} Product price object
     */
    originalPrice() {
      let price;

      if (hasPriceRange(this.product)) {
        price = productPrice(this.product.price.max.sales);
      } else if (hasDefaultDiscount(this.product)) {
        price = productPrice(this.product.price.list);
      }

      return price;
    },

    /**
     * Get the current product price
     * @returns {Object} Product price object
     */
    price() {
      return productPrice(
        hasPriceRange(this.product)
          ? this.product.price.min.sales
          : this.product.price.sales
      );
    },

    /**
     * Get the best previous product price
     * @returns {Object|null} Product price object
     */
    priorBestPrice() {
      const currentPrice = hasPriceRange(this.product)
        ? this.product.price.max.priorBestPrice
        : this.product.price.priorBestPrice;
      return currentPrice ? productPrice(currentPrice) : null;
    },

    /**
     * Get the additional info text product price
     * @returns {Object} Price additional info text object
     */
    additionalInfoPrice() {
      return {
        text:
          this.product.price?.max?.additionalInfo ||
          this.product.price?.additionalInfo ||
          null,
        tooltip:
          this.product.price?.max?.additionalInfoTooltip ||
          this.product.price?.additionalInfoTooltip ||
          null,
      };
    },
  },

  methods: {
    /**
     * Returns true if the product has any kind of discount
     * @returns {boolean} True if it has discount
     */
    hasDiscount() {
      return this.discountPercent > 0;
    },
  },
};

// TODO REFACTOR TO USE PRICE OBJECT AS A PROP INSTEAD OF FULL PRODUCT
export default {
  props: {
    product: {
      type: Object,
      required: true,
      default() {
        return {};
      },
    },
  },
  ...productPriceMixin,
};
